import React from 'react';
import { MdZoomIn } from '@react-icons/all-files/md/MdZoomIn';
import { MdZoomOut } from '@react-icons/all-files/md/MdZoomOut';
import { Slider } from '@/components/ui/slider';
import AvatarEditor from 'react-avatar-editor';
import Dropzone from 'react-dropzone';
import { cn } from '@/utils/utils';
import { Button } from '@/components/ui/button';
import { MdDeleteForever } from '@react-icons/all-files/md/MdDeleteForever';
import useMeasure from '@/hooks/useMeasure';
import { FaUpload } from '@react-icons/all-files/fa/FaUpload';
import { FaCloudUploadAlt } from '@react-icons/all-files/fa/FaCloudUploadAlt';
import { useFormikContext } from 'formik';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';

function convertRemToPixels(rem: number) {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

export type LogoEditorProps = {
  name: string;
  aspectRatio?: number;
  minHeight?: number;
};

const MIN_BORDER = 25;

export const LogoEditor: React.FC<LogoEditorProps> = ({
  name,
  aspectRatio = 3,
  minHeight = 250,
}) => {
  const editor = React.useRef<AvatarEditor>(null);
  const { values, setFieldValue } = useFormikContext();
  const [image, setImage] = React.useState<string | File | undefined>(
    () => (values as any)[name]
  );
  const [scale, setScale] = React.useState(1);
  const [{ ref }, width] = useMeasure('clientWidth');
  const height = minHeight;
  const tooWide =
    (width - 2 * MIN_BORDER) / (height - 2 * MIN_BORDER) > aspectRatio;
  const vBorder = React.useMemo(
    () =>
      !tooWide
        ? (height - (width - 2 * MIN_BORDER) / aspectRatio) / 2
        : MIN_BORDER,
    [aspectRatio, height, tooWide, width]
  );
  const hBorder = React.useMemo(
    () =>
      !tooWide
        ? MIN_BORDER
        : (width - aspectRatio * (height - 2 * MIN_BORDER)) / 2,
    [aspectRatio, height, tooWide, width]
  );
  const logoWidth = React.useMemo(
    () => Math.max(width - 2 * hBorder - convertRemToPixels(2.75), 100),
    [hBorder, width]
  );
  const logoHeight = React.useMemo(
    () => Math.max(logoWidth / aspectRatio, 100),
    [aspectRatio, logoWidth]
  );

  const getImageOrUndef = React.useCallback(() => {
    try {
      return editor.current?.getImage().toDataURL();
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }, []);

  const throttledChangeHandler = useDebouncedCallback(
    () => setFieldValue(name, getImageOrUndef()),
    500
  );

  return (
    <div ref={ref} className={cn('flex flex-row gap-4 w-full')}>
      <Dropzone
        onDrop={(dropped) => setImage(dropped[0] as any)}
        accept="image/*"
        noClick
        noKeyboard
      >
        {({ getRootProps, getInputProps, open }) => (
          <>
            <div className={cn('w-full relative')} {...getRootProps()}>
              <AvatarEditor
                ref={editor}
                width={logoWidth}
                height={logoHeight}
                image={image ?? ''}
                scale={scale}
                className={cn('rounded-md relative')}
                color={[0, 0, 0, 0.35]}
                borderRadius={2}
                border={[hBorder, vBorder]}
                onImageChange={throttledChangeHandler}
                crossOrigin="anonymous"
                disableBoundaryChecks
              />
              <input {...getInputProps()} />
              {!image && (
                <div
                  className={cn(
                    'absolute top-0 left-0 w-full h-full',
                    'flex flex-col items-center justify-center',
                    'rounded-md border-blue-300 border-4 border-dashed bg-blue-100',
                    'dark:bg-sidebar-accent dark:border-sidebar-accent-foreground/30',
                    `p-4`
                  )}
                >
                  <p className={cn('max-w-md text-center')}>
                    Arrastrá una imágen aquí para representar tu empresa, o
                    presioná el botón para buscar archivos.
                  </p>
                  <Button
                    variant="success"
                    onClick={() => {
                      open();
                    }}
                  >
                    <FaCloudUploadAlt size={24} />
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-col min-h-full items-center gap-2">
              <Button
                variant="ghost"
                size="sm-icon"
                onClick={() => setScale((scale) => Math.min(scale + 0.1, 2))}
                disabled={!image}
              >
                <MdZoomIn size={30} />
              </Button>
              <Slider
                orientation="vertical"
                className={cn('grow')}
                value={[scale]}
                min={0}
                max={2}
                step={0.01}
                onValueChange={(value) => setScale(value[0])}
                disabled={!image}
              />
              <Button
                variant="ghost"
                size="sm-icon"
                onClick={() => setScale((scale) => Math.max(0, scale - 0.1))}
                disabled={!image}
              >
                <MdZoomOut size={30} />
              </Button>
              <Button
                variant="destructive-outline"
                size="sm-icon"
                onClick={() => {
                  setImage(undefined);
                  setFieldValue(name, undefined);
                }}
                disabled={!image}
              >
                <MdDeleteForever size={30} />
              </Button>
              <Button
                variant="success-outline"
                size="sm-icon"
                onClick={() => open()}
                disabled={!!image}
              >
                <FaUpload size={15} />
              </Button>
            </div>
          </>
        )}
      </Dropzone>
    </div>
  );
};
